import { nonEmptyTrimmedStringFromUnknown, optionalBooleanQuerySchema } from "../protocol/shared.js";
import { profileKindSchema } from "../protocol/profile.js";
import { runtimeCoordinatorExecutionRecordSchema } from "../daemon/runtime-coordinator-execution.js";
import { clientMessageIdSchema } from "../protocol/client-message-id.js";
import { agentInteractionPendingSummarySchema } from "../protocol/agent-interaction.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/space-wake-progress.ts
const optionalSelectorSchema = z$1.preprocess((value) => {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : void 0;
}, z$1.string().min(1).optional());
const wakeProgressPhaseSchema = z$1.enum([
	"sending",
	"request_sent",
	"queued",
	"connecting",
	"running",
	"writing",
	"publishing",
	"publication_unknown",
	"retrying",
	"failed",
	"stopped",
	"replied"
]);
const WAKE_PROGRESS_CONTROLLER_CONVERSATION_OCCUPIED_REASON_CODE = "runtime.agent_controller_conversation.occupied";
const wakeProgressWaitReasonCodeSchema = z$1.enum([WAKE_PROGRESS_CONTROLLER_CONVERSATION_OCCUPIED_REASON_CODE]);
const wakeStopEligibilityReasonCodeSchema = z$1.enum([
	"agent_controller_interface.cancel_required",
	"agent_controller_interface.missing",
	"agent_controller_interface.stop_unsupported",
	"runtime_coordinator.pre_agent_stop_available",
	"runtime_coordinator.pre_agent_stop_unavailable"
]);
const wakeStopEligibilityReadModelSchema = z$1.union([
	z$1.strictObject({
		controlMode: z$1.literal("openmeld_owned_publication_cancel"),
		reasonCode: z$1.null(),
		reliability: z$1.literal("openmeld_owned_publication"),
		status: z$1.literal("eligible")
	}),
	z$1.strictObject({
		controlMode: z$1.literal("openmeld_owned_pre_agent_cancel"),
		reasonCode: z$1.null(),
		reliability: z$1.literal("openmeld_owned_pre_agent"),
		status: z$1.literal("eligible")
	}),
	z$1.strictObject({
		controlMode: z$1.literal("cancel"),
		reasonCode: z$1.null(),
		reliability: z$1.literal("native_cancel"),
		status: z$1.literal("eligible")
	}),
	z$1.strictObject({
		controlMode: z$1.literal("interrupt"),
		reasonCode: z$1.null(),
		reliability: z$1.literal("native_interrupt"),
		status: z$1.literal("eligible")
	}),
	z$1.strictObject({
		controlMode: z$1.null(),
		reasonCode: wakeStopEligibilityReasonCodeSchema,
		reliability: z$1.literal("unsupported"),
		status: z$1.literal("not_eligible")
	})
]);
const wakeProgressItemSchema = z$1.strictObject({
	deviceWait: z$1.strictObject({
		deadlineAtMs: z$1.number().int().nonnegative(),
		reason: z$1.enum([
			"device_offline",
			"agent_not_ready",
			"earlier_work",
			"access_check_unavailable",
			"service_draining"
		])
	}).optional(),
	clientMessageId: clientMessageIdSchema.nullable(),
	sourceSignalId: z$1.string().trim().min(1).nullable(),
	sourceContext: z$1.strictObject({
		messagePreview: z$1.string().trim().min(1).nullable(),
		profileId: z$1.string().trim().min(1),
		profileKind: profileKindSchema,
		profileName: z$1.string().trim().min(1).nullable(),
		threadId: z$1.string().trim().min(1).nullable()
	}).nullable().optional(),
	dispatchId: z$1.string().trim().min(1).nullable(),
	runtimeCoordinatorExecutionId: runtimeCoordinatorExecutionRecordSchema.shape.runtimeCoordinatorExecutionId.nullable().optional(),
	targetProfileId: z$1.string().trim().min(1),
	targetLabel: z$1.string().trim().min(1),
	phase: wakeProgressPhaseSchema,
	failureReasonCode: z$1.string().trim().min(1).nullable().optional(),
	message: z$1.string().trim().min(1),
	previewText: z$1.string().trim().min(1).optional(),
	startedAtMs: z$1.int().min(0).optional(),
	updatedAtMs: z$1.int().min(0),
	terminal: z$1.boolean(),
	waitReasonCode: wakeProgressWaitReasonCodeSchema.nullable().optional(),
	wakeStopEligibility: wakeStopEligibilityReadModelSchema.nullable().optional(),
	pendingInteraction: agentInteractionPendingSummarySchema.nullable().optional()
});
const getSpaceWakeProgressInputSchema = z$1.strictObject({
	spaceId: nonEmptyTrimmedStringFromUnknown,
	clientMessageId: clientMessageIdSchema,
	includePreviewText: optionalBooleanQuerySchema,
	includeTiming: optionalBooleanQuerySchema,
	includeSourceContext: optionalBooleanQuerySchema,
	includeWaitReason: optionalBooleanQuerySchema,
	includeDeviceWait: optionalBooleanQuerySchema,
	includeWakeStopEligibility: optionalBooleanQuerySchema,
	targetProfileId: optionalSelectorSchema
});
const getSpaceWakeProgressResponseSchema = z$1.strictObject({
	query: z$1.strictObject({
		spaceId: z$1.string().trim().min(1),
		clientMessageId: clientMessageIdSchema.nullable(),
		targetProfileId: z$1.string().trim().min(1).nullable()
	}),
	items: z$1.array(wakeProgressItemSchema)
});
const getActiveSpaceWakeProgressInputSchema = z$1.strictObject({
	spaceId: nonEmptyTrimmedStringFromUnknown,
	includePageInfo: optionalBooleanQuerySchema,
	includePreviewText: optionalBooleanQuerySchema,
	includeTiming: optionalBooleanQuerySchema,
	includeSourceContext: optionalBooleanQuerySchema,
	includeWaitReason: optionalBooleanQuerySchema,
	includeDeviceWait: optionalBooleanQuerySchema,
	includeWakeStopEligibility: optionalBooleanQuerySchema,
	sourceProfileId: optionalSelectorSchema,
	targetProfileId: optionalSelectorSchema
});
const getActiveSpaceWakeProgressResponseSchema = z$1.strictObject({
	query: z$1.strictObject({
		spaceId: z$1.string().trim().min(1),
		sourceProfileId: z$1.string().trim().min(1).nullable().optional(),
		targetProfileId: z$1.string().trim().min(1).nullable()
	}),
	pageInfo: z$1.strictObject({
		hasMore: z$1.boolean(),
		limit: z$1.int().min(1)
	}).optional(),
	items: z$1.array(wakeProgressItemSchema)
});
//#endregion
export { WAKE_PROGRESS_CONTROLLER_CONVERSATION_OCCUPIED_REASON_CODE, getActiveSpaceWakeProgressInputSchema, getActiveSpaceWakeProgressResponseSchema, getSpaceWakeProgressInputSchema, getSpaceWakeProgressResponseSchema, wakeProgressItemSchema, wakeProgressPhaseSchema, wakeProgressWaitReasonCodeSchema, wakeStopEligibilityReadModelSchema, wakeStopEligibilityReasonCodeSchema };

//# sourceMappingURL=space-wake-progress.js.map